/*
 * PresentacionBajadaRenta.jsx
 *
 * Presentación ejecutiva del programa "Bajada de Renta con Palanca Preventiva"
 * pensada para presentar al cliente (TOTEM) y convencerle de escalarlo a toda
 * la cartera.
 *
 * Se apoya en 2 casos reales (los que están activos, ej. LE0006 y CX0001) y
 * proyecta el ROI a los N candidatos identificados en la cartera.
 *
 * Salida: PDF real (jsPDF + html2canvas) con formato horizontal 16:9 estilo
 * slides corporativas.
 */
import React, { useRef, useState, useMemo, useEffect, useLayoutEffect } from 'react';
import { XCircle, Download, Loader2, Presentation } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  PieChart as RcPieChart, Pie, Cell, Tooltip, LabelList,
} from 'recharts';

const eur = (v) => (Number(v) || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 });
const eur1 = (v) => (Number(v) || 0).toLocaleString('es-ES', { maximumFractionDigits: 1 });
const todayLong = () => new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });

// Formatea años decimales como "4a 5m" (más natural que "4.4 años").
const yearsLabel = (yearsDecimal) => {
  const y = Math.floor(yearsDecimal);
  const m = Math.round((yearsDecimal - y) * 12);
  if (y === 0) return `${m} meses`;
  if (m === 0) return y === 1 ? '1 año' : `${y} años`;
  return `${y}a ${m}m`;
};

// ---------------------------------------------------------------------------
// Cálculo de KPIs por negociación (mismo criterio que la ficha detalle)
// ---------------------------------------------------------------------------
const computeNegKpis = (neg, site) => {
  const curMonthly = Number(neg.current_rent_monthly) || 0;
  const finMonthly = Number(neg.final_rent_monthly ?? neg.target_rent_monthly) || 0;
  const currentAnnual = curMonthly * 12;
  const proposedAnnual = finMonthly * 12;
  const annualSaving = Math.max(0, currentAnnual - proposedAnnual);
  const reductionPct = currentAnnual > 0 ? (annualSaving / currentAnnual) * 100 : 0;

  // Preferimos la fecha de vencimiento REAL del site (cartera TOTEM) sobre la
  // snapshot guardada en la negociación, que puede haber quedado obsoleta.
  const contractEnd = site?.contract_end || neg.contract_end_date || null;
  let years = Number(neg.service_offer?.duration_years) || 1;
  if (contractEnd) {
    const end = new Date(contractEnd);
    if (!isNaN(end.getTime())) {
      years = Math.max(0, (end.getTime() - Date.now()) / (365.25 * 24 * 60 * 60 * 1000));
    }
  }

  const items = neg.service_offer?.items || [];
  // Coste ANUAL de servicios preventivos: para cada ítem, valor de mercado
  // dividido por la frecuencia en años (ej. 1.500 € cada 2 años → 750 €/año).
  const servicesAnnual = items.reduce((s, it) => {
    const val = Number(it.market_value_eur) || 0;
    const freqYears = (Number(it.frequency_months) || 12) / 12;
    return freqYears > 0 ? s + val / freqYears : s;
  }, 0);
  // Coste TOTAL a lo largo de la ventana temporal.
  const servicesTotal = servicesAnnual * years;
  const totalRentSaved = annualSaving * years;
  const netTotem = totalRentSaved - servicesTotal;
  const netAnnual = annualSaving - servicesAnnual;
  const ownerBenefit = totalRentSaved + servicesTotal;

  return {
    curMonthly, finMonthly, currentAnnual, proposedAnnual, annualSaving, reductionPct,
    years, contractEnd, items, servicesAnnual, servicesTotal,
    totalRentSaved, netAnnual, netTotem, ownerBenefit,
  };
};

// ---------------------------------------------------------------------------
// Presentación
// ---------------------------------------------------------------------------
const PresentacionBajadaRenta = ({ negotiations, sites, kpi, candidates, onClose }) => {
  const printRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  // Escalado responsive: los slides miden 1280×720 (para PDF), pero en pantallas
  // más pequeñas los escalamos vía CSS transform sin perder resolución del export.
  // Estado inicial calculado desde window.innerWidth para evitar flash de contenido
  // sin escalar (crítico en móviles con la URL bar visible).
  const initialScale = typeof window !== 'undefined'
    ? Math.min(1, Math.max(0.2, (window.innerWidth - 24) / 1280))
    : 0.3;
  const [scale, setScale] = useState(initialScale);
  const previewRef = useRef(null);
  useLayoutEffect(() => {
    const compute = () => {
      const containerWidth = previewRef.current?.clientWidth || window.innerWidth || 1280;
      const available = Math.max(280, containerWidth - 8);
      setScale(Math.min(1, available / 1280));
    };
    compute();
    // Observer sobre el propio contenedor: cubre reflows del modal + rotación.
    let ro;
    if (typeof ResizeObserver !== 'undefined' && previewRef.current) {
      ro = new ResizeObserver(compute);
      ro.observe(previewRef.current);
    }
    window.addEventListener('resize', compute);
    window.addEventListener('orientationchange', compute);
    const t1 = setTimeout(compute, 80);
    const t2 = setTimeout(compute, 300);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('orientationchange', compute);
      clearTimeout(t1); clearTimeout(t2);
      if (ro) ro.disconnect();
    };
  }, []);

  // Casos de estudio: priorizamos LE0006 y CX0001 (casos consolidados en cartera).
  // Si no existen como negociaciones, caemos a las 2 primeras con oferta definida.
  const cases = useMemo(() => {
    const negs = negotiations || [];
    const preferredCodes = ['LE0006', 'CX0001'];
    const preferred = preferredCodes
      .map((code) => negs.find((n) => n.site_id === code))
      .filter(Boolean);
    const withOffer = negs.filter((n) => (n.service_offer?.items?.length || 0) > 0);
    const fallback = withOffer.length >= 2 ? withOffer : negs;
    // Rellenamos hasta 2: primero los preferidos, luego el resto (sin duplicar).
    const merged = [...preferred];
    for (const n of fallback) {
      if (merged.length >= 2) break;
      if (!merged.find((x) => x.id === n.id)) merged.push(n);
    }
    const siteByCode = new Map((sites || []).map((s) => [s.code, s]));
    return merged.slice(0, 2).map((neg) => {
      const site = siteByCode.get(neg.site_id) || null;
      const k = computeNegKpis(neg, site);
      return { ...k, neg, site };
    });
  }, [negotiations, sites]);

  // Extrapolación a la cartera completa (candidatos identificados por el motor).
  const projection = useMemo(() => {
    const avgReductionPct = cases.length ? cases.reduce((s, c) => s + c.reductionPct, 0) / cases.length : 12;
    const avgServiceCostPerYear = cases.length && cases[0].years > 0
      ? cases.reduce((s, c) => s + c.servicesTotal / (c.years || 1), 0) / cases.length
      : 800;
    const potentialAnnual = kpi?.savings_potential_annual_eur || 0;
    const candidatesCount = candidates?.length || 0;
    // 3 escenarios: conservador 25% éxito, base 45%, ambicioso 65%.
    const scenarios = [
      { name: 'Conservador', pct: 0.25 },
      { name: 'Base', pct: 0.45 },
      { name: 'Ambicioso', pct: 0.65 },
    ].map((s) => ({
      ...s,
      signed: Math.round(candidatesCount * s.pct),
      annualSavings: Math.round(potentialAnnual * s.pct),
      annualServiceCost: Math.round(candidatesCount * s.pct * avgServiceCostPerYear),
      netAnnual: Math.round(potentialAnnual * s.pct - candidatesCount * s.pct * avgServiceCostPerYear),
    }));
    return { avgReductionPct, avgServiceCostPerYear, potentialAnnual, candidatesCount, scenarios };
  }, [cases, kpi, candidates]);

  // ---- Export PDF (paisaje 16:9) ----
  const exportPDF = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      const slides = printRef.current.querySelectorAll('[data-slide]');
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      for (let i = 0; i < slides.length; i++) {
        const canvas = await html2canvas(slides[i], {
          scale: 2, backgroundColor: '#0a0a0a', useCORS: true, logging: false,
        });
        const img = canvas.toDataURL('image/jpeg', 0.92);
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        if (i > 0) pdf.addPage();
        pdf.addImage(img, 'JPEG', 0, 0, imgWidth, imgHeight);
      }
      pdf.save(`Presentacion_BajadaRenta_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Presentación PDF generada');
    } catch (e) {
      console.error(e);
      toast.error('Error generando el PDF');
    } finally { setExporting(false); }
  };

  return (
    <div className="fixed inset-0 z-[10080] bg-black/95 backdrop-blur flex items-center justify-center p-2 sm:p-4"
      onClick={onClose} data-testid="presentation-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[1400px] max-h-[96vh] flex flex-col bg-[#0a0a0a] border border-white/15 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-white/10 bg-gradient-to-r from-[#A3FF12]/10 to-transparent">
          <div className="flex items-center gap-2 min-w-0">
            <Presentation size={16} className="text-[#A3FF12]"/>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#A3FF12] font-black">Presentación ejecutiva</div>
              <div className="text-white text-sm font-black truncate">
                Programa Bajada de Renta · Palanca Preventiva
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={exportPDF} disabled={exporting}
              data-testid="btn-export-presentation-pdf"
              className="bg-[#A3FF12] hover:bg-[#b8ff3a] text-black text-xs font-black px-3 py-2 rounded-lg flex items-center gap-1 disabled:opacity-40">
              {exporting ? <Loader2 className="animate-spin" size={12}/> : <Download size={12}/>}
              {exporting ? 'Generando…' : 'Descargar PDF'}
            </button>
            <button onClick={onClose} data-testid="btn-close-presentation"
              className="text-white/50 hover:text-white p-1"><XCircle size={22}/></button>
          </div>
        </div>

        {/* Preview */}
        <div ref={previewRef} className="flex-1 overflow-x-hidden overflow-y-auto p-1 sm:p-4 bg-neutral-800/40">
          <div ref={printRef} className="mx-auto flex flex-col items-center gap-5"
            style={{ width: `${Math.round(1280 * scale)}px`, maxWidth: '100%' }}>
            <ScaledSlide scale={scale}><SlideCover/></ScaledSlide>
            <ScaledSlide scale={scale}><SlideConcept/></ScaledSlide>
            <ScaledSlide scale={scale}><SlideMethodology/></ScaledSlide>
            {cases.map((c, i) => (
              <ScaledSlide key={i} scale={scale}><SlideCase k={c} idx={i + 1} totalCases={cases.length}/></ScaledSlide>
            ))}
            <ScaledSlide scale={scale}><SlideProjection projection={projection}/></ScaledSlide>
            <ScaledSlide scale={scale}><SlideRoadmap/></ScaledSlide>
            <ScaledSlide scale={scale}><SlideAsk projection={projection}/></ScaledSlide>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Slides
// ============================================================================

// Wrapper que aplica CSS scale al slide para que quepa en pantallas pequeñas,
// pero mantiene el tamaño real 1280×720 en el DOM (crítico para que html2canvas
// exporte el PDF a resolución completa). Ajusta el height del contenedor para
// que no queden huecos entre slides.
const ScaledSlide = ({ scale, children }) => (
  <div style={{
    width: `${Math.round(1280 * scale)}px`,
    height: `${Math.round(720 * scale)}px`,
    overflow: 'hidden',
    maxWidth: '100%',
    flexShrink: 0,
  }}>
    <div style={{
      width: '1280px',
      height: '720px',
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
    }}>
      {children}
    </div>
  </div>
);

// Cada slide se renderiza a 1280×720 (16:9) para el export a PDF horizontal.
const Slide = ({ children, dark = true, footer, page }) => (
  <div data-slide
    className="relative shadow-2xl"
    style={{
      width: '1280px', minHeight: '720px',
      background: dark ? '#0a0a0a' : '#ffffff',
      color: dark ? '#f8fafc' : '#0f172a',
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      padding: '54px 64px',
      overflow: 'hidden',
    }}>
    {children}
    <SlideFooter dark={dark} page={page}/>
  </div>
);

const SlideFooter = ({ dark, page }) => (
  <div style={{
    position: 'absolute', bottom: '24px', left: '64px', right: '64px',
    display: 'flex', justifyContent: 'space-between', fontSize: '10px',
    letterSpacing: '3px', color: dark ? '#64748b' : '#94a3b8',
    borderTop: dark ? '1px solid #1f2937' : '1px solid #e2e8f0', paddingTop: '10px',
  }}>
    <span>ADQUIRIAM · CONFIDENCIAL · {todayLong().toUpperCase()}</span>
    {page && <span>{page}</span>}
  </div>
);

const AdquiriamMark = ({ dark = true, size = 'lg' }) => {
  const fontSize = size === 'lg' ? '38px' : size === 'md' ? '26px' : '18px';
  return (
    <div style={{
      fontWeight: 900, letterSpacing: '5px', fontSize,
      color: dark ? '#f8fafc' : '#0f172a',
    }}>
      ADQUIR<span style={{ color: '#A3FF12' }}>I</span>AM
    </div>
  );
};

// -------- Portada --------
const SlideCover = () => (
  <Slide dark page="01">
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '580px' }}>
      <AdquiriamMark size="lg"/>
      <div>
        <div style={{ fontSize: '12px', letterSpacing: '5px', color: '#A3FF12', fontWeight: 900, marginBottom: '18px' }}>
          PROPUESTA ESTRATÉGICA · CONFIDENCIAL
        </div>
        <div style={{ fontSize: '62px', fontWeight: 900, lineHeight: 1.05, letterSpacing: '-1px' }}>
          Programa de <span style={{ color: '#A3FF12' }}>Bajada de Renta</span>
          <br/> con palanca preventiva
        </div>
        <div style={{ fontSize: '18px', color: '#cbd5e1', marginTop: '22px', maxWidth: '760px', lineHeight: 1.5 }}>
          Escalando el modelo probado a toda la cartera TOTEM: reducimos OPEX
          arrendaticio a la vez que profesionalizamos el mantenimiento del
          emplazamiento. Un único trade-off con dos ganadores.
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div style={{ fontSize: '13px', color: '#64748b' }}>
          Preparado por Adquiriam · Dpto. Gestión de Cartera
        </div>
        <div style={{ fontSize: '13px', color: '#94a3b8' }}>{todayLong()}</div>
      </div>
    </div>
  </Slide>
);

// -------- Concepto --------
const SlideConcept = () => {
  return (
    <Slide dark page="02">
      <SlideTitle idx="01" title="El concepto · Ahorro real recurrente"/>
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '46px', marginTop: '22px', height: '480px' }}>
        <div>
          <div style={{ fontSize: '28px', fontWeight: 900, lineHeight: 1.3, color: '#f8fafc' }}>
            Proponemos una <span style={{ color: '#A3FF12' }}>bajada de renta entre el 8% y el 10%</span> y,
            a cambio, TOTEM se ocupa de <span style={{ color: '#A3FF12' }}>prevenir y reparar los deterioros que la instalación</span> genera sobre el inmueble.
          </div>
          <div style={{ fontSize: '14px', color: '#cbd5e1', marginTop: '22px', lineHeight: 1.65, maxWidth: '560px' }}>
            Realizando <b style={{ color: '#f8fafc' }}>visitas concertadas con la Propiedad</b>,
            reforzamos la confianza y consolidamos la relación, generando un
            <b style={{ color: '#f8fafc' }}> ahorro sustancial en la renta</b> y, en
            consecuencia a largo plazo, <b style={{ color: '#A3FF12' }}>minorizando el riesgo
            de repliegue</b> al vencimiento del contrato.
          </div>
          <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '14px', lineHeight: 1.55, maxWidth: '560px', fontStyle: 'italic' }}>
            La diferencia entre la bajada y el coste de las intervenciones preventivas es beneficio
            anual recurrente. Multiplicado por los años restantes del contrato, es el ahorro
            estructural del contrato.
          </div>
          {/* Ecuación conceptual */}
          <div style={{
            marginTop: '30px', border: '1px solid #1f2937', borderRadius: '14px',
            padding: '18px 20px', background: '#0f172a',
            display: 'grid', gridTemplateColumns: '1fr auto 1fr auto 1fr', alignItems: 'center', gap: '10px',
          }}>
            <FlowStep label="Ahorro renta" value="Bajada anual" color="#A3FF12"/>
            <FlowArrow>−</FlowArrow>
            <FlowStep label="Coste servicios" value="Preventivos" color="#f97316"/>
            <FlowArrow>=</FlowArrow>
            <FlowStep label="Neto TOTEM anual" value="Ahorro recurrente" color="#A3FF12" bold/>
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          border: '1px solid #A3FF12', borderRadius: '18px', padding: '30px',
        }}>
          <div style={{ fontSize: '11px', letterSpacing: '3px', color: '#A3FF12', fontWeight: 900, marginBottom: '18px' }}>
            RESULTADOS ESPERADOS
          </div>
          <MetricRow label="Reducción media" value="10–15%" note="sobre renta anual"/>
          <MetricRow label="Tasa firma esperada" value="45%" note="basado en pilotos"/>
          <MetricRow label="Coste in-kind" value="≈ 30%" note="del ahorro anual"/>
          <MetricRow label="Neto TOTEM anual" value="+70%" note="del ahorro bruto" accent/>
        </div>
      </div>
    </Slide>
  );
};

const FlowStep = ({ label, value, color, bold }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '9px', letterSpacing: '2px', color: '#64748b', textTransform: 'uppercase', fontWeight: 900 }}>
      {label}
    </div>
    <div style={{ fontSize: bold ? '22px' : '18px', fontWeight: 900, color, marginTop: '4px' }}>
      {value}
    </div>
  </div>
);
const FlowArrow = ({ children }) => (
  <div style={{
    color: '#94a3b8', fontSize: '20px', fontWeight: 900, textAlign: 'center', width: '20px',
  }}>{children}</div>
);

const MetricRow = ({ label, value, note, accent }) => (
  <div style={{ padding: '12px 0', borderBottom: '1px solid #1f2937', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
    <div>
      <div style={{ fontSize: '12px', color: '#94a3b8', letterSpacing: '2px', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{note}</div>
    </div>
    <div style={{ fontSize: '30px', fontWeight: 900, color: accent ? '#A3FF12' : '#f8fafc' }}>{value}</div>
  </div>
);

// -------- Metodología --------
const SlideMethodology = () => {
  const steps = [
    { n: '01', title: 'Selección', text: 'Motor Adquiriam filtra la cartera por renta anual, años restantes y rentabilidad del site.' },
    { n: '02', title: 'Diagnóstico', text: 'Análisis normativo del inmueble: ITE, accesibilidad, cubiertas, patologías. Base de argumentario legal.' },
    { n: '03', title: 'Diseño de la oferta', text: 'Paquete de servicios preventivos con valor de mercado equivalente a la bajada objetivo.' },
    { n: '04', title: 'Negociación', text: 'Dosier corporativo al propietario con la propuesta económica + anexo normativo.' },
    { n: '05', title: 'Firma & ejecución', text: 'Adenda al contrato. Adquiriam programa las intervenciones y coordina proveedores.' },
    { n: '06', title: 'Seguimiento', text: 'Panel único para TOTEM: KPIs, forecast de ahorro, evolución vs objetivo.' },
  ];
  return (
    <Slide dark page="03">
      <SlideTitle idx="02" title="Cómo funciona · flujo Adquiriam"/>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginTop: '30px' }}>
        {steps.map((s) => (
          <div key={s.n} style={{
            border: '1px solid #1f2937', borderRadius: '14px', padding: '22px 22px 24px',
            background: '#0f172a',
          }}>
            <div style={{ fontSize: '11px', letterSpacing: '3px', color: '#A3FF12', fontWeight: 900 }}>{s.n}</div>
            <div style={{ fontSize: '22px', fontWeight: 900, marginTop: '6px', marginBottom: '10px' }}>{s.title}</div>
            <div style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: 1.5 }}>{s.text}</div>
          </div>
        ))}
      </div>
    </Slide>
  );
};

// -------- Caso real --------
const SlideCase = ({ k, idx, totalCases }) => {
  const chartData = [
    { name: 'Ahorro renta', value: k.totalRentSaved, color: '#A3FF12' },
    { name: 'Coste servicios', value: k.servicesTotal, color: '#f97316' },
    { name: 'Ahorros netos', value: Math.max(0, k.netTotem), color: '#38bdf8' },
  ];
  const endLabel = k.contractEnd ? String(k.contractEnd).slice(0, 10) : 'duración pactada';
  return (
    <Slide dark page={`04${totalCases > 1 ? `-${idx}` : ''}`}>
      <SlideTitle idx={`03${totalCases > 1 ? `.${idx}` : ''}`} title={`Caso real ${idx}/${totalCases} · ${k.neg.site_id}`}/>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '16px', height: '486px' }}>
        {/* Panel numérico */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* ---- Bloque ANUAL ---- */}
          <div>
            <div style={{ fontSize: '10px', letterSpacing: '3px', color: '#A3FF12', fontWeight: 900, marginBottom: '8px' }}>
              CIFRAS ANUALES
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <KpiBox label="Renta inicial" value={`${eur(k.currentAnnual)} €/año`}/>
              <KpiBox label={`Bajada propuesta · ${k.reductionPct.toFixed(1)}%`} value={`−${eur(k.annualSaving)} €/año`} accent/>
              <KpiBox label="Coste preventivo" value={`${eur(k.servicesAnnual)} €/año`} color="#f97316"/>
              <KpiBox label="Ahorro TOTEM" value={`+${eur(k.netAnnual)} €/año`} accent/>
            </div>
          </div>
          {/* ---- Bloque TOTAL HASTA FIN DE CONTRATO ---- */}
          <div>
            <div style={{ fontSize: '10px', letterSpacing: '3px', color: '#A3FF12', fontWeight: 900, marginBottom: '8px' }}>
              TOTAL HASTA FIN DE CONTRATO · {endLabel} · {yearsLabel(k.years)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
              <KpiBox label="Bajada renta" value={`${eur(k.totalRentSaved)} €`}/>
              <KpiBox label="Coste preventivo" value={`${eur(k.servicesTotal)} €`} color="#f97316"/>
              <KpiBox label="Ahorros netos" value={`+${eur(k.netTotem)} €`} accent/>
            </div>
          </div>
          {/* ---- Servicios preventivos ---- */}
          <div style={{
            padding: '12px 16px', background: '#0f172a', borderRadius: '12px', border: '1px solid #1f2937',
          }}>
            <div style={{ fontSize: '10px', letterSpacing: '2.5px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 900, marginBottom: '4px' }}>
              Servicios preventivos ofrecidos
            </div>
            {k.items.length === 0 ? (
              <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '12px', marginTop: '4px' }}>
                Oferta pendiente de definir
              </div>
            ) : (
              <ul style={{ margin: '4px 0 0', paddingLeft: '16px', color: '#cbd5e1', fontSize: '12px', lineHeight: 1.5 }}>
                {k.items.slice(0, 4).map((it, i) => {
                  const freqY = (Number(it.frequency_months) || 12) / 12;
                  const annual = freqY > 0 ? (Number(it.market_value_eur) || 0) / freqY : 0;
                  return (
                    <li key={i}>
                      {it.title}
                      <span style={{ color: '#64748b' }}> · {eur(it.market_value_eur)} € cada {freqY >= 1 ? `${freqY} año${freqY > 1 ? 's' : ''}` : `${it.frequency_months} m`}</span>
                      <span style={{ color: '#f97316', fontWeight: 700 }}> → {eur(annual)} €/año</span>
                    </li>
                  );
                })}
                {k.items.length > 4 && <li style={{ color: '#64748b', fontStyle: 'italic' }}>+{k.items.length - 4} más…</li>}
              </ul>
            )}
          </div>
        </div>
        {/* Gráfico */}
        <div style={{
          border: '1px solid #1f2937', borderRadius: '14px', padding: '24px',
          background: '#0f172a', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ fontSize: '11px', letterSpacing: '3px', color: '#A3FF12', fontWeight: 900, marginBottom: '10px' }}>
            BALANCE ECONÓMICO · HASTA FIN DE CONTRATO
          </div>
          <div style={{ flex: 1, minHeight: '360px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 24, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937"/>
                <XAxis dataKey="name" stroke="#94a3b8" fontSize={12}/>
                <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `${eur(v)} €`} width={72}/>
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {chartData.map((d, i) => <Cell key={i} fill={d.color}/>)}
                  <LabelList dataKey="value" position="top" fill="#f8fafc" fontSize={13} fontWeight={900}
                    formatter={(v) => `${eur(v)} €`}/>
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px', textAlign: 'center' }}>
            Fin de contrato: <b style={{ color: '#cbd5e1' }}>{endLabel}</b> · Ventana: {yearsLabel(k.years)}
          </div>
        </div>
      </div>
    </Slide>
  );
};

const KpiBox = ({ label, value, accent, color }) => (
  <div style={{
    border: '1px solid #1f2937', borderRadius: '12px', padding: '12px 14px',
    background: '#0f172a',
  }}>
    <div style={{ fontSize: '9.5px', letterSpacing: '2px', color: '#64748b', textTransform: 'uppercase', fontWeight: 900, lineHeight: 1.3 }}>
      {label}
    </div>
    <div style={{ fontSize: '22px', fontWeight: 900, marginTop: '4px', color: color || (accent ? '#A3FF12' : '#f8fafc') }}>
      {value}
    </div>
  </div>
);

// -------- Proyección al portfolio --------
const SlideProjection = ({ projection }) => {
  const chartData = projection.scenarios.map((s) => ({
    name: s.name,
    Ahorro: s.annualSavings,
    Coste: s.annualServiceCost,
    Neto: s.netAnnual,
  }));
  return (
    <Slide dark page="05">
      <SlideTitle idx="04" title="Proyección a cartera completa"/>
      <div style={{ display: 'flex', gap: '32px', marginTop: '12px', height: '470px' }}>
        {/* Explicación */}
        <div style={{ width: '340px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: 1.6 }}>
            El motor Adquiriam ha identificado <b style={{ color: '#f8fafc' }}>{projection.candidatesCount} candidatos</b> con
            potencial de ajuste en la cartera. Aplicando los ratios observados en
            los casos reales, la proyección anual de ahorro neto en 3 escenarios
            de tasa de firma es la siguiente.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px', marginTop: '18px' }}>
            {projection.scenarios.map((s) => (
              <div key={s.name} style={{
                border: `1px solid ${s.name === 'Base' ? '#A3FF12' : '#1f2937'}`,
                borderRadius: '10px', padding: '12px 14px',
                background: s.name === 'Base' ? '#A3FF1210' : '#0f172a',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: '13px', fontWeight: 900 }}>{s.name}</div>
                  <div style={{ fontSize: '10px', color: '#94a3b8', letterSpacing: '2px', textTransform: 'uppercase' }}>
                    {(s.pct * 100).toFixed(0)}% firma
                  </div>
                </div>
                <div style={{ fontSize: '18px', fontWeight: 900, color: s.name === 'Base' ? '#A3FF12' : '#f8fafc', marginTop: '2px' }}>
                  +{eur(s.netAnnual)} €/año neto
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                  {s.signed} sites firmados · ahorro bruto {eur(s.annualSavings)} €
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Gráfico */}
        <div style={{
          flex: 1, border: '1px solid #1f2937', borderRadius: '14px', padding: '24px',
          background: '#0f172a', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ fontSize: '11px', letterSpacing: '3px', color: '#A3FF12', fontWeight: 900, marginBottom: '10px' }}>
            AHORRO ANUAL VS COSTE VS NETO
          </div>
          <div style={{ flex: 1, minHeight: '360px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 30, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937"/>
                <XAxis dataKey="name" stroke="#94a3b8" fontSize={13}/>
                <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `${eur(v)} €`} width={72}/>
                <Bar dataKey="Ahorro" fill="#A3FF12" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="Ahorro" position="top" fill="#A3FF12" fontSize={11} fontWeight={900}
                    formatter={(v) => `${eur(v)} €`}/>
                </Bar>
                <Bar dataKey="Coste" fill="#f97316" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="Coste" position="top" fill="#f97316" fontSize={11} fontWeight={900}
                    formatter={(v) => `${eur(v)} €`}/>
                </Bar>
                <Bar dataKey="Neto" fill="#38bdf8" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="Neto" position="top" fill="#38bdf8" fontSize={11} fontWeight={900}
                    formatter={(v) => `${eur(v)} €`}/>
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '4px', textAlign: 'center' }}>
            Cifras anuales · asumiendo reducción media {projection.avgReductionPct.toFixed(1)}% y coste servicios ≈ {eur(projection.avgServiceCostPerYear)} €/site·año
          </div>
        </div>
      </div>
    </Slide>
  );
};

// -------- Roadmap --------
const SlideRoadmap = () => {
  const phases = [
    { q: 'Q1', title: 'Piloto ampliado', desc: '25 emplazamientos, mix Norte/Sur. Refinar oferta y guion de negociación.' },
    { q: 'Q2', title: 'Escalado 200 sites', desc: 'Onboarding proveedores servicios. Automatización dosier + normativa.' },
    { q: 'Q3', title: 'Cobertura 1.000 sites', desc: 'Panel ejecutivo TOTEM en vivo. Renegociación proactiva pre-vencimiento.' },
    { q: 'Q4', title: 'Programa continuo', desc: '3.000+ sites/año. Integración con módulo Preventivo y Bajada 2.0.' },
  ];
  return (
    <Slide dark page="06">
      <SlideTitle idx="05" title="Roadmap 12 meses"/>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '18px', marginTop: '40px' }}>
        {phases.map((p, i) => (
          <div key={p.q} style={{
            border: '1px solid #1f2937', borderRadius: '14px', padding: '24px',
            background: i === 0 ? '#A3FF1215' : '#0f172a', position: 'relative',
          }}>
            <div style={{
              position: 'absolute', top: '-16px', left: '20px',
              background: i === 0 ? '#A3FF12' : '#1e293b', color: i === 0 ? '#0a0a0a' : '#f8fafc',
              padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 900, letterSpacing: '2px',
            }}>{p.q}</div>
            <div style={{ fontSize: '18px', fontWeight: 900, marginTop: '8px' }}>{p.title}</div>
            <div style={{ fontSize: '12.5px', color: '#cbd5e1', marginTop: '10px', lineHeight: 1.55 }}>{p.desc}</div>
          </div>
        ))}
      </div>
      <div style={{
        marginTop: '46px', padding: '22px 26px', border: '1px solid #A3FF12',
        borderRadius: '14px', background: '#A3FF1210',
      }}>
        <div style={{ fontSize: '11px', letterSpacing: '3px', color: '#A3FF12', fontWeight: 900 }}>PRINCIPIO OPERATIVO</div>
        <div style={{ fontSize: '20px', fontWeight: 700, marginTop: '6px', color: '#f8fafc', lineHeight: 1.4 }}>
          Cada trimestre re-evaluamos KPI reales vs proyección. Si la tasa de firma
          o el neto se desvía &gt;15%, ajustamos la horquilla del preventivo antes de escalar.
        </div>
      </div>
    </Slide>
  );
};

// -------- Cierre / compromiso --------
const SlideAsk = ({ projection }) => (
  <Slide dark page="07">
    <SlideTitle idx="06" title="Petición de compromiso"/>
    <div style={{ marginTop: '30px', display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '38px' }}>
      <div>
        <div style={{ fontSize: '32px', fontWeight: 900, lineHeight: 1.2 }}>
          Necesitamos <span style={{ color: '#A3FF12' }}>arrancar el escalado</span> con
          25 emplazamientos seleccionados por Adquiriam antes del cierre de trimestre.
        </div>
        <ul style={{ marginTop: '26px', paddingLeft: '18px', color: '#cbd5e1', fontSize: '15px', lineHeight: 1.75 }}>
          <li>Aprobación del <b style={{ color: '#f8fafc' }}>presupuesto in-kind</b> (~{eur(projection.avgServiceCostPerYear * 25)} €/año).</li>
          <li>Delegación al equipo Adquiriam de la <b style={{ color: '#f8fafc' }}>firma de adendas</b> hasta 15% de reducción.</li>
          <li>Compromiso de <b style={{ color: '#f8fafc' }}>revisión trimestral</b> conjunta para ampliar cobertura.</li>
        </ul>
      </div>
      <div style={{
        padding: '30px', border: '1px solid #A3FF12', borderRadius: '18px',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      }}>
        <div style={{ fontSize: '11px', letterSpacing: '3px', color: '#A3FF12', fontWeight: 900 }}>
          IMPACTO ESPERADO EN 12 MESES
        </div>
        <div style={{ fontSize: '15px', color: '#cbd5e1', marginTop: '10px' }}>Escenario base</div>
        <div style={{ fontSize: '52px', fontWeight: 900, color: '#A3FF12', marginTop: '10px', lineHeight: 1 }}>
          +{eur(projection.scenarios[1].netAnnual)} €/año
        </div>
        <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '4px' }}>ahorro neto TOTEM</div>
        <div style={{ height: '1px', background: '#1f2937', margin: '22px 0' }}/>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <MiniStat label="Sites firmados" value={projection.scenarios[1].signed}/>
          <MiniStat label="% reducción media" value={`${projection.avgReductionPct.toFixed(1)}%`}/>
        </div>
      </div>
    </div>
    <div style={{
      position: 'absolute', bottom: '54px', left: '64px', right: '64px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      paddingTop: '20px', borderTop: '1px solid #1f2937',
    }}>
      <AdquiriamMark size="sm"/>
      <div style={{ fontSize: '11px', color: '#64748b', letterSpacing: '2px' }}>
        adquiriam.com · Gestión integral de emplazamientos
      </div>
    </div>
  </Slide>
);

const MiniStat = ({ label, value }) => (
  <div>
    <div style={{ fontSize: '10px', letterSpacing: '2px', color: '#64748b', textTransform: 'uppercase', fontWeight: 900 }}>
      {label}
    </div>
    <div style={{ fontSize: '22px', fontWeight: 900, color: '#f8fafc', marginTop: '2px' }}>{value}</div>
  </div>
);

const SlideTitle = ({ idx, title }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: '18px', marginBottom: '6px' }}>
    <div style={{ fontSize: '13px', letterSpacing: '4px', color: '#A3FF12', fontWeight: 900 }}>{idx}</div>
    <div style={{ fontSize: '30px', fontWeight: 900, letterSpacing: '-0.5px' }}>{title}</div>
    <div style={{ flex: 1, height: '1px', background: '#1f2937', marginLeft: '14px' }}/>
  </div>
);

export default PresentacionBajadaRenta;
